import { useEffect, useMemo, useState } from "react";
import { createGfEntry } from "../../../api/giftingClient";
import { STAGE_NAMES, campaignAddBlocker, handleOf, primarySns } from "../../../lib/giftingRules";
import type { GfAddEntryModalProps, GfStage } from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { GfButton, GfField, GfModal } from "../ui/GfUi";

/** 開始段階の選択肢（打診中=1 / 条件合意=3 / 住所フォーム送付=4） */
const START_STAGES: ReadonlyArray<{ stage: 1 | 3 | 4; label: string }> = [
  { stage: 1, label: `${STAGE_NAMES[1]}（これから打診する）` },
  { stage: 3, label: `${STAGE_NAMES[3]}（条件は合意済み）` },
  { stage: 4, label: "住所フォーム送付（フォームURLを渡した）" },
];

/** クリエイター（複数可）× 案件 × 開始段階 → エントリーを人数分作成する。営業タブからも使う */
export function AddEntryModal({ open, onClose, influencerIds, onSaved, data, nav, refresh, toast }: GfAddEntryModalProps) {
  const { creators, campaigns } = data.board;
  const activeCampaigns = useMemo(() => campaigns.filter((c) => c.status === "active"), [campaigns]);
  const [picked, setPicked] = useState<string[]>([]);
  const [campaignId, setCampaignId] = useState("");
  const [startStage, setStartStage] = useState<1 | 3 | 4>(1);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPicked(influencerIds);
    const preset = nav.state.campaign !== "all" && activeCampaigns.some((c) => c.id === nav.state.campaign) ? nav.state.campaign : "";
    setCampaignId(preset || activeCampaigns[0]?.id || "");
    setStartStage(1);
    setQ("");
    setError(null);
    // open の切替時だけ初期化する（入力中に board が更新されても選択を消さない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sorted = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return [...creators]
      .filter((c) => !needle || c.name.toLowerCase().includes(needle) || c.sns.some((s) => s.handle.toLowerCase().includes(needle)))
      .sort((a, b) => a.name.localeCompare(b.name, "ja"));
  }, [creators, q]);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!picked.length) return setError("クリエイターを選んでください。");
    if (!campaignId) return setError("案件を選んでください。");
    const blocker = campaignAddBlocker(activeCampaigns.find((c) => c.id === campaignId));
    if (blocker) return setError(blocker);
    setBusy(true);
    setError(null);
    const created: string[] = [];
    const failed: Array<{ id: string; text: string }> = [];
    const nameOf = (id: string) => creators.find((c) => c.id === id)?.name ?? id;
    for (const id of picked) {
      try {
        const r = await createGfEntry({ influencer_id: id, campaign_id: campaignId, stage: startStage });
        created.push(r.id);
      } catch (e) {
        failed.push({ id, text: `${nameOf(id)}: ${errMsg(e)}` });
      }
    }
    await refresh();
    setBusy(false);
    if (created.length) onSaved(created);
    if (failed.length === 0) {
      toast(`${created.length}件を「${STAGE_NAMES[startStage as GfStage]}」で案件に追加しました`);
      onClose();
      return;
    }
    // 失敗分だけ選択に残して再実行できるようにする
    setPicked(failed.map((f) => f.id));
    setError(`${created.length}件を追加、${failed.length}件は失敗しました。${failed.map((f) => f.text).join(" ／ ")}`);
  };

  return (
    <GfModal
      open={open}
      title="案件にクリエイターを追加"
      onClose={onClose}
      footer={
        <>
          <GfButton onClick={onClose} disabled={busy}>
            やめる
          </GfButton>
          <GfButton variant="primary" disabled={busy || !picked.length || !campaignId} onClick={() => void submit()}>
            {busy ? "追加中…" : `${picked.length}名を追加`}
          </GfButton>
        </>
      }
    >
      {activeCampaigns.length === 0 ? (
        <div className="gf-banner is-info">進行中の案件がありません。先に「新規案件」を作成してください。</div>
      ) : (
        <>
          <GfField label="案件">
            <select className="gf-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              {activeCampaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </GfField>
          <GfField label="開始段階">
            <select className="gf-select" value={startStage} onChange={(e) => setStartStage(Number(e.target.value) as 1 | 3 | 4)}>
              {START_STAGES.map((s) => (
                <option key={s.stage} value={s.stage}>
                  {s.label}
                </option>
              ))}
            </select>
          </GfField>
          <div className="gf-field">
            <span className="gf-field-label">クリエイター（複数選択可）</span>
            <input className="gf-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前・ハンドルで絞り込み" />
            <div className="gf-pick-list">
              {sorted.length === 0 && <div className="gf-empty">該当するクリエイターがいません</div>}
              {sorted.map((c) => (
                <label key={c.id} className="gf-check gf-pick-row">
                  <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} />
                  <span className="gf-pick-name">{c.name}</span>
                  <span className="gf-pick-handle">{handleOf(primarySns(c.sns))}</span>
                </label>
              ))}
            </div>
            <span className="gf-field-hint">{picked.length}名を選択中</span>
          </div>
        </>
      )}
      {error && <div className="gf-banner is-error">{error}</div>}
    </GfModal>
  );
}
